import { Drawer, IconButton, Box, Typography, Button, CircularProgress, Grid, Checkbox, FormControlLabel, Chip, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UserIcon from '@mui/icons-material/Person'
import WSSelectField from '../../Components/Select/WSSelect';
import { callAPI, customValidation, getLocalValue, LoacalVariables, Method, validations } from '../../commonFunctions/CommonFunctions';
import WSTextField, { TextType } from '../../Components/TextBox/WSTextBox';
import { useAuth } from '../../StateManagement/authContext';
import { useForm } from 'react-hook-form';
import { useSnackbar } from '../../Components/Snackbar/SnackBarContext';
import { useEffect, useState } from 'react';
import { resetDataComponent } from '../../StateManagement/actions';
import { useDispatch } from 'react-redux';
import UserSuccessModal from './UserCredentialPopup';

interface UserFormDrawerProps {
    open: boolean;
    onClose: () => void;
    id?: string;
}

const UserFormDrawer = ({ open, onClose, id }: UserFormDrawerProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const { user: userData } = useAuth();
    const userType = getLocalValue(LoacalVariables.UserType);
    const { control, handleSubmit, reset, watch, setValue } = useForm();
    const { showMessage } = useSnackbar();
    const password = watch("password");

    const [loading, setLoading] = useState(false);
    // const [createdUser, setCreatedUser] = useState(null); // holds user info to display
    // const [showUserModal, setShowUserModal] = useState(false); // modal visibility

    const [loadingRegion, setLoadingRegion] = useState(false);
    const [districtOptions, setDistrictOptions] = useState<any[]>([]);
    const [blockOptions, setBlockOptions] = useState<any[]>([]);
    const [cdsOptions, setCdsOptions] = useState<any[]>([]);
    const [enablePasswordEdit, setEnablePasswordEdit] = useState<boolean>(true);

    const selectedDistrict = watch("districtId");
    const selectedBlock = watch("blockId");

    useEffect(() => {
        if (userType === 'block') {
            setValue('role', 'cds');
        }
    }, [])

    const fetchDistricts = async () => {
        try {
            const res = await callAPI("districts/getAll", Method.GET);
            setDistrictOptions(
                res?.data?.map((d: any) => ({ label: d.name, value: d._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch districts:", error);
            showMessage("Failed to load districts", "error");
        }
    };

    useEffect(() => {
        if (selectedDistrict) {
            fetchBlocks(selectedDistrict);
            reset({ blockId: "", cdsId: "" }, { keepValues: true }); // clear lower levels
            setCdsOptions([]); // clear CDS options
        }
    }, [selectedDistrict]);

    useEffect(() => {
        if (userType === 'admin' && selectedBlock) {
            
            fetchCDS(selectedBlock);
            reset({ cdsId: "" }, { keepValues: true }); // clear CDS on block change
        } else if (userType === 'block' && userData?.block) {
            fetchCDS(userData.block);
        }
    }, [selectedBlock]);

    const fetchBlocks = async (districtId: string) => {
        try {
            setLoadingRegion(true);
            const res = await callAPI(`blocks/byDistrict/${districtId}`, Method.GET);
            setBlockOptions(
                res?.data?.map((b: any) => ({ label: b.name, value: b._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch blocks:", error);
            showMessage("Failed to load blocks", "error");
        } finally {
            setLoadingRegion(false);
        }
    };

    const fetchCDS = async (blockId: string) => {
        try {
            const res = await callAPI(`cds/byBlock/${blockId}`, Method.GET);
            
            setCdsOptions(
                res?.map((c: any) => ({ label: c.name, value: c._id })) || []
            );
        } catch (error) {
            console.error("Failed to fetch CDS:", error);
            showMessage("Failed to load CDS", "error");
        }
    };

    useEffect(() => {
        if (userType === "admin") {
            fetchDistricts();
        } else if (userType === "district") {
            const districtId = getLocalValue(LoacalVariables.District);
            if (districtId) {
                reset((prev) => ({
                    ...prev,
                    districtId,
                }), { keepValues: true });
                fetchBlocks(districtId);
            }
        }
    }, []);

    useEffect(() => {
        const role = watch("role");
        if (role === "admin") {
            reset({ districtId: "", blockId: "", cdsId: "" }, { keepValues: true });
        } else if (role === "district") {
            reset({ blockId: "", cdsId: "" }, { keepValues: true });
        } else if (role === "block") {
            reset({ cdsId: "" }, { keepValues: true });
        }
    }, [watch("role")]);

    useEffect(() => {
        const fetchDataById = async () => {
            setLoading(true);
            try {
                const response: any = await callAPI(
                    "users/getBy",
                    Method.GET,
                    {},
                    id
                );

                const user = response?.user;
                

                if (user) {
                    setValue("name", user.name || "");
                    setValue("email", user.email || "");
                    setValue("mobile", user.mobile || "");
                    setValue("role", user.role || "");

                    // Set role-specific fields
                    if (userType === 'admin' || userType === 'district') {
                        setValue("districtId", userData?.district || "");
                        if (userData?.district) {
                            await fetchBlocks(userData?.district);
                        }
                    }else{
                        setValue('blockId', user.block._id)
                    }
                }

            } catch (error: any) {
                console.error("Failed to retrieve data", error);
                if (error.message === 'GlobalHandledError') return;
                showMessage(
                    error?.response?.data?.message || "Failed to retrieve data",
                    "error"
                );
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchDataById();
    }, [id]);

    const handleData = async (data: any) => {
        
        try {
            setLoading(true);

            const request: any = {
                name: data.name,
                mobile: data.mobile,
                email: data.email || undefined,
                password: data.password,
                newRole: data.role,
            };

            // Inject hierarchy location
            if (data.districtId) request.district = data.districtId;
            if (data.blockId) request.block = data.blockId;
            if (data.cdsId) request.cds = data.cdsId;


            const method = id ? Method.PUT : Method.POST;
            const url = id
                ? `users/update/${id}`
                : "users/register";

            const response: any = await callAPI(url, method, request);

            if (response.success) {
                showMessage(response.message, "success");
                // if (!id) {
                //     setCreatedUser({ ...request }); // include password
                //     setShowUserModal(true);
                // }
                reset();
                onClose();
                dispatch(resetDataComponent("usersGrid"));
            }

        } catch (e: any) {
            console.error("Error saving user data:", e);
            showMessage(
                e?.response?.data?.message || "An unexpected error occurred.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };


    const getButtonText = () => {
        if (loading && id) return "Updating...";
        if (loading) return "Creating...";
        if (id) return "Update User";
        return "Create New User";
    };


    const roleOptions = [
        { label: "District", value: "district" },
        { label: "Block", value: "block" },
        { label: "CDS", value: "cds" },
    ]


    const getCreatableRoles = (userType: string) => {
        switch (userType) {
            case "admin":
                return ["district", "block", "cds"];
            case "district":
                return ["cds"];
            case "block":
                return ["cds"];
            default:
                return [];
        }
    };

    const allowedRoles = getCreatableRoles(getLocalValue(LoacalVariables.UserType));
    const filteredRoleOptions = roleOptions.filter((role) =>
        allowedRoles.includes(role.value)
    );

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', md: '650px' },
                    background: 'linear-gradient(195deg, #f9fafb 0%, #f0f2f5 100%)',
                    boxShadow: '0px 0px 30px rgba(0, 0, 0, 0.1)',
                    borderLeft: '1px solid rgba(0, 0, 0, 0.08)'
                }
            }}
        >
            {/* Drawer Header */}
            <Box sx={{
                p: 3,
                background: 'white',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        background: 'linear-gradient(195deg, #4AD16F 0%, #08B1B6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                    }}>
                        <UserIcon fontSize="small" />
                    </Box>
                    <Typography variant="h6" fontWeight={600} color="#022324">
                        {id ? 'Update User' : 'Create New User'}
                    </Typography>
                    {id && <Chip label="Edit Mode" size="small" sx={{ ml: 1, background: 'rgba(167, 26, 104, 0.1)', color: '#4AD16F' }} />}

                </Box>
                <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Form Content */}
            <Box sx={{
                p: 3,
                height: 'calc(100vh - 80px)',
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.divider,
                    borderRadius: '3px'
                }
            }}>
                <form onSubmit={handleSubmit(handleData)}>
                    <Grid container spacing={3}>
                        {/* Personal Information Section */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={500} mb={2} sx={{
                                color: '#022324',
                                display: 'flex',
                                alignItems: 'center',
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    width: '4px',
                                    height: '16px',
                                    background: 'linear-gradient(195deg, #4AD16F 0%, #08B1B6 100%)',
                                    borderRadius: '4px',
                                    mr: 1.5
                                }
                            }}>
                                Personal Information
                            </Typography>
                            <Box sx={{
                                p: 3,
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.03)',
                                border: '1px solid rgba(0, 0, 0, 0.05)'
                            }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <WSTextField
                                            control={control}
                                            id="name"
                                            name="name"
                                            label="Full Name *"
                                            textIcon="Person"
                                            rules={customValidation(true, validations.name)}
                                            disabled={loading}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <WSTextField
                                            id="mobile"
                                            label="Mobile Number *"
                                            type={TextType.AllowOnlyNumber}
                                            name="mobile"
                                            control={control}
                                            rules={customValidation(true, validations.phone)}
                                            textIcon="Call"
                                            disabled={loading}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <WSTextField
                                            control={control}
                                            id="email"
                                            name="email"
                                            label="Email *"
                                            textIcon="Email"
                                            rules={customValidation(true, validations.email)}
                                            disabled={loading}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>

                        {/* Password Section */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={500} mb={2} sx={{
                                color: '#022324',
                                display: 'flex',
                                alignItems: 'center',
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    width: '4px',
                                    height: '16px',
                                    background: 'linear-gradient(195deg, #4DD26B 0%, #08B1B6 100%)',
                                    borderRadius: '4px',
                                    mr: 1.5
                                }
                            }}>
                                {id ? 'Update Password' : 'Set Password'}
                            </Typography>
                            {id && (
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={enablePasswordEdit}
                                                onChange={(e) => setEnablePasswordEdit(e.target.checked)}
                                                sx={{
                                                    color: '#4DD26B',
                                                    '&.Mui-checked': { color: '#08B1B6' }
                                                }}
                                            />
                                        }
                                        label="Update Password"
                                        disabled={loading}
                                    />
                                </Grid>
                            )}
                            {(enablePasswordEdit || id) && (
                                <Box sx={{
                                    p: 3,
                                    backgroundColor: 'white',
                                    borderRadius: '12px',
                                    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.03)',
                                    border: '1px solid rgba(0, 0, 0, 0.05)'
                                }}>
                                    <Grid container spacing={2}>
                                        {enablePasswordEdit && (
                                            <>
                                                <Grid item xs={12} md={6}>
                                                    <WSTextField
                                                        label="Create Password *"
                                                        id="password"
                                                        name="password"
                                                        control={control}
                                                        type={TextType.Password}
                                                        textIcon="Password"
                                                        rules={customValidation(true, validations.password)}
                                                        disabled={loading}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <WSTextField
                                                        label="Confirm Password *"
                                                        id="confirmPassword"
                                                        name="confirmPassword"
                                                        control={control}
                                                        type={TextType.Password}
                                                        textIcon="Password"
                                                        rules={{
                                                            required: "Please confirm your password",
                                                            validate: (value) =>
                                                                value === password || "Passwords do not match",
                                                        }}
                                                        disabled={loading}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Minimum 8 characters with at least one number & special character (@#$%^&*)
                                                    </Typography>
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Box>
                            )}
                        </Grid>

                        {/* Role & Location Section */}
                        {userType !== 'cds' && !id && <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight={500} mb={2} sx={{
                                color: '#022324',
                                display: 'flex',
                                alignItems: 'center',
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    width: '4px',
                                    height: '16px',
                                    background: 'linear-gradient(195deg, #4AD16F 0%, #08B1B6 100%)',
                                    borderRadius: '4px',
                                    mr: 1.5
                                }
                            }}>
                                Role & Location
                            </Typography>
                            <Box sx={{
                                p: 3,
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.03)',
                                border: '1px solid rgba(0, 0, 0, 0.05)'
                            }}>
                                <Grid container spacing={2}>
                                    {getLocalValue(LoacalVariables.UserType) === "admin" && <Grid item xs={12}>
                                        <WSSelectField
                                            control={control}
                                            id="role"
                                            name="role"
                                            label="Select Role *"
                                            rules={{ required: true }}
                                            options={filteredRoleOptions}
                                            disabled={loading}
                                        />
                                    </Grid>}

                                    {/* Conditional Location Fields */}
                                    {["district", "block", "cds"].includes(watch("role")) &&
                                        getLocalValue(LoacalVariables.UserType) === "admin" && (
                                            <Grid item xs={12} md={6}>
                                                <WSSelectField
                                                    control={control}
                                                    id="districtId"
                                                    name="districtId"
                                                    label="Select District *"
                                                    rules={{ required: true }}
                                                    options={districtOptions}
                                                />
                                            </Grid>
                                        )}

                                    {["block", "cds"].includes(watch("role")) && userType !== 'block' &&  (
                                        <Grid item xs={12} md={6}>
                                            <WSSelectField
                                                control={control}
                                                id="blockId"
                                                name="blockId"
                                                label="Select Block *"
                                                rules={{ required: true }}
                                                loader={loadingRegion}
                                                options={blockOptions}
                                            />
                                        </Grid>
                                    )}

                                    {watch("role") === "cds" && (
                                        <Grid item xs={12}>
                                            <WSSelectField
                                                control={control}
                                                id="cdsId"
                                                name="cdsId"
                                                label="Select CDS *"
                                                rules={{ required: true }}
                                                options={cdsOptions}
                                                loader={loadingRegion}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Grid>
                        }

                        {/* Submit Button */}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{
                                    height: '48px',
                                    background: 'linear-gradient(195deg, #4AD16F 0%, #08B1B6 100%)',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    boxShadow: '0px 3px 5px rgba(167, 26, 104, 0.2)',
                                    '&:hover': {
                                        boxShadow: '0px 5px 10px rgba(167, 26, 104, 0.3)',
                                    },
                                    '&:disabled': {
                                        background: '#e0e0e0',
                                        color: '#9e9e9e'
                                    }
                                }}
                                disabled={loading}
                            >
                                {loading ? (
                                    <CircularProgress size={24} sx={{ color: 'white' }} />
                                ) : (
                                    getButtonText()
                                )}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>

            {/* {showUserModal && (
                <UserSuccessModal
                    createdUser={createdUser}
                    open={showUserModal}
                    onClose={() => setShowUserModal(false)}
                />
            )} */}
        </Drawer>
    );
};

export default UserFormDrawer;